import React from 'react'
import first from 'lodash/first'
import last from 'lodash/last'
import css from './timeSelector.module.scss'
import { Checkbox } from '@blueprintjs/core'
import { getDefaultTimeValues } from '@utils'
import { DateUtil, TIME } from '@utils/dateUtil'
import TimeSelector from 'components/NewSettings/TimeSelector'
import { getDisabledOptionsIndexes } from './hooks/useTimeSelector'
import useSettings from 'components/NewSettings/hooks/useSettings'

type TimeSelectorParamT = {
  allDay: boolean
  selected: DateUtil[]
  timezone: string
  defaultTimeFrom: string
  defaultTimeTo: string
  bookedTimes: DateUtil[]
  onTimeChange: (param: { fromDate: DateUtil; toDate: DateUtil }) => void
  onAllDayChange: (param: boolean) => void
  isNotBookDesk: boolean
}

const regex = /(\d{1,2}):(\d{2}) (\w{2})/
const CalendarTimeSelector = ({
  allDay,
  selected = [],
  timezone,
  defaultTimeFrom,
  defaultTimeTo,
  bookedTimes,
  onTimeChange,
  onAllDayChange,
  isNotBookDesk,
}: TimeSelectorParamT) => {
  const { defaultHourFrom, defaultMinuteFrom, defaultHourTo, defaultMinuteTo } =
    getDefaultTimeValues({
      bookingWorkdayTimeFrom: defaultTimeFrom,
      bookingWorkdayTimeTo: defaultTimeTo,
    })
  const settings = useSettings()
  const startDate = first(selected) as DateUtil
  const endDate = last(selected) as DateUtil

  const startFromTimeOfDay = startDate
    .clone()
    .set({ hour: defaultHourFrom, minute: defaultMinuteFrom, second: 0, millisecond: 0 })

  const endFromTimeOfDay = startDate
    .clone()
    .set({ hour: defaultHourTo, minute: defaultMinuteTo, second: 0, millisecond: 0 })

  const endToTimeOfDay = endDate
    .clone()
    .set({ hour: defaultHourTo, minute: defaultMinuteTo, second: 0, millisecond: 0 })

  const fromList: DateUtil[] = [startFromTimeOfDay]
  const toList: DateUtil[] = [endFromTimeOfDay]
  let time
  let fromProcessed = false
  for (let i = 1; i < 24 * 4; i++) {
    time = fromList[0].clone().add(15 * i, TIME.MINUTES)

    if (time.isSameOrBefore(endFromTimeOfDay)) {
      fromList.push(time)
    } else {
      fromProcessed = true
    }
    if (time.isSameOrBefore(endToTimeOfDay)) {
      toList.push(time)
    } else {
      if (fromProcessed) {
        break
      }
    }
  }

  const disabledInds = getDisabledOptionsIndexes({
    timeFromList: fromList,
    timeToList: toList,
    timezone,
    bookedTimes,
    startTime: startDate,
    endTime: endDate,
    bookingWorkdayTimeFrom: defaultTimeFrom,
    bookingWorkdayTimeTo: defaultTimeTo,
  })

  return (
    <div className={css.wrapper}>
      {(!settings.dailyBookingEnabled || settings.hourlyBookingEnabled) && (
        <>
          <TimeSelector
            timezone={timezone}
            timeList={fromList}
            disabledFromOptions={disabledInds.disabledFrom}
            disabledToOptions={disabledInds.disabledTo}
            showDate={true}
            timeFrom={startDate}
            dateProps={{
              style: {
                fontSize: 14,
                border: '1px solid #e5e7eb',
                borderRadius: 5,
                paddingBottom: 5,
                width: 70,
                display: 'inline-block',
                textAlign: 'center',
              },
              className: 'py-1',
            }}
            buttonProps={{
              style: { fontSize: 14, fontWeight: 400, background: 'white', width: 90 },
            }}
            timeTo={endDate}
            disableFrom={allDay}
            disableTo={allDay}
            onChange={(value, isFrom) => {
              const [, hour, minute, am] = regex.exec(value) as Array<string>
              const isAM = am.toLowerCase() === 'am'
              let hr = Number(hour)

              // If the hour is 12 and it's AM, it should be 0 in 24-hour format (midnight)
              if (hr === 12 && isAM) {
                hr = 0
              }

              // If it's PM and the hour is not 12, add 12 to convert to 24-hour format
              if (!isAM && hr !== 12) {
                hr += 12
              }
              if (isFrom) {
                startDate.set({
                  hour: hr,
                  minute: Number(minute),
                })
              } else {
                endDate.set({
                  hour: hr,
                  minute: Number(minute),
                })
              }
              onTimeChange({ fromDate: startDate, toDate: endDate })
            }}
            isNotBookDesk={isNotBookDesk}
          />
          {settings.dailyBookingEnabled && (
            <Checkbox
              checked={allDay}
              className={css.checkbox}
              size={15}
              onChange={(ev) => {
                onAllDayChange(ev.currentTarget.checked)
              }}
            />
          )}
        </>
      )}
    </div>
  )
}

export default CalendarTimeSelector
